import TenantComponents from '@components';
import tenantConstants from '@constants';
import tenantData from '@data';
import tenantTheme from '@theme';
import tenantUtils from '@utils';
import { Badge, Row, Space, Typography } from 'antd';
import { t } from 'i18next';
import React, { useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isUnifiedProfolioEnabled } from '../../../../utility/user';
import PlatformListing from '../../../../components/table/table-components/platform-listings';
import { getBaseURL } from '../../../../utility/env';
import { HealthContainer } from './styled';
import PlatformActions from './listing-platform-actions/platformActions';
import { TextWithIcon, Divider, Flex, Group, Image, Number, ProductTag, Tag } from '../../../../components/common';
import ListingDrawer from '../../../../components/listing-drawer/listingDrawer';
import { ListingCardStyled, Thumbnail } from '../../../../components/styled';
import { UnifiedListingTableActions } from '../../../../components/table/table-components/unified-listing-table-actions';
import { MobileListingStatsBar } from './mobileListingStatsBar';
import { getAppliedListingProductSlugFromProductsInfo, getMergedListingProductsInfo } from '../../transformers/listings';
import { getUnifiedListingTierProductTagProps } from '../../utils/unifiedListingTierBadge';

const { Text } = Typography;

const getUnifiedMobileStatsSlug = (platforms) => {
  if (platforms?.unified?.showStats) return 'unified';
  if (platforms?.bayut?.showStats) return 'bayut';
  if (platforms?.dubizzle?.showStats) return 'dubizzle';
  return null;
};

const ListingCard = ({ item, disableDiv, selected, wrap, align }) => {
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);
  const { user: loginUser } = useSelector((state) => state.app.loginUser);
  const listingDrawerRef = useRef();
  const isDailyRentalListing = item?.purpose?.slug == 'daily-rental';
  const rtl = useSelector((state) => state.app.AppConfig.rtl);
  const isUnifiedListings = isUnifiedProfolioEnabled({ featureKey: 'listingsUnified' });

  const getTagProps = () => {
    const { productsInfo = {} } = item?.property;

    if (productsInfo?.['signature-listing']?.is_applied) {
      return tenantData.getListingActions('signature-listing');
    }
    if (productsInfo?.['hot-listing']?.is_applied) {
      return tenantData.getListingActions('hot-listing');
    }
    return tenantData.getListingActions('basic-listing');
  };

  const unifiedListingTypeChip = useMemo(() => {
    if (!isUnifiedListings) return null;
    const slug = getAppliedListingProductSlugFromProductsInfo(getMergedListingProductsInfo(item));
    if (!slug) return null;
    const tagProps = tenantData.getListingActions(slug);
    if (!tagProps) return null;
    return getUnifiedListingTierProductTagProps(slug, tagProps);
  }, [isUnifiedListings, item]);

  const handleClick = () => {
    listingDrawerRef?.current && listingDrawerRef.current.open(item?.id);
  };

  const unifiedMobileStatsSlug =
    isMobile && isUnifiedListings ? getUnifiedMobileStatsSlug(item?.platforms) : null;

  const firstPlatformSlugForInlineActions =
    isMobile && isUnifiedListings
      ? item?.platforms?.unified
        ? 'unified'
        : item?.platforms?.bayut
          ? 'bayut'
          : item?.platforms?.dubizzle
            ? 'dubizzle'
            : null
      : null;

  const platformActionsInlineSlot =
    isMobile && isUnifiedListings ? (
      <PlatformActions
        {...item?.platforms}
        isUnifiedListings
        unifiedListingActionLabels={isUnifiedListings}
        unifiedMobileInlineRow
      />
    ) : null;

  return (
    <div>
      <ListingCardStyled
        style={{
          ...(disableDiv && { pointerEvents: 'none', opacity: 0.4 }),
          ...(selected && { backgroundColor: '#e7f3ef' }),
        }}
      >
        <Group template="106px 1fr" gap=".571rem" className="mb-8">
          <Badge
            count={item?.images_count}
            size="default"
            overflowCount={90}
            offset={!rtl && [-105, 0]}
            style={{ backgroundColor: '#00a651' }}
            shape="square"
          >
            <Thumbnail>
              <Image
                onClick={handleClick}
                src={item?.image?.thumbnail}
                fallback={`${getBaseURL()}/profolio-assets/images/ph-listings.svg`}
              />
            </Thumbnail>
          </Badge>
          <Group template="initial" gap="4px">
            <Row align={align || 'middle'} wrap={wrap} style={{ flexWrap: 'wrap', gap: isMobile && '6px' }}>
              <div className="mb-2" style={{ position: 'relative', width: '100%' }}>
                <Space style={{ paddingInlineEnd: isMobile && isUnifiedListings ? 12 : undefined }}>
                  <div className="fz-14 fw-700" style={{ lineHeight: 1.2 }}>
                    <Number className="text-primary" compact={false} type="price" {...item?.property?.price} />
                    {isDailyRentalListing && (
                      <span
                        className="fw-500"
                        style={{ marginInlineStart: '4px', fontSize: '11px', color: tenantTheme['primary-color'] }}
                      >
                        {t('Night')}
                      </span>
                    )}
                  </div>
                  {isUnifiedListings && unifiedListingTypeChip && !isMobile && (
                    <ProductTag {...unifiedListingTypeChip} />
                  )}
                  {!isUnifiedListings && item?.property?.productsInfo?.['basic-listing']?.is_applied && (
                    <ProductTag {...getTagProps()} iconProps={{ ...getTagProps().iconProps }} />
                  )}
                </Space>
                {isMobile && isUnifiedListings && (
                  <div style={{ position: 'absolute', insetInlineEnd: 1, top: 0 }}>
                    <UnifiedListingTableActions {...item?.platforms} listingRow={item} menuOnly />
                  </div>
                )}
              </div>
              <span>
                {item?.listing_type?.title && item?.listing_purpose?.title && (
                  <Tag
                    color={tenantTheme['primary-light-4']}
                    style={{ '--tag-color': tenantTheme['primary-color'], fontWeight: '700' }}
                  >
                    {`${tenantUtils.getLocalisedString(item?.listing_type, 'title')} ${t(`for ${item?.listing_purpose?.title}`)}${
                      tenantConstants.SHOW_COMPLETION_STATUS_FOR_SELL &&
                      item?.property?.purpose?.slug === 'sale' &&
                      !!item?.property?.completion_status
                        ? ` | ${t(item?.property?.projectStatus)}`
                        : ''
                    }`}
                  </Tag>
                )}
              </span>
              <HealthContainer style={{ alignItems: 'start' }}>
                <>
                  {item?.health && <TenantComponents.Health {...item?.health} listingsData={item?.property} isMobile />}
                </>
              </HealthContainer>
            </Row>
            <div>
              <Space size="large">
                {item?.beds > 0 && (
                  <TextWithIcon
                    icon="IconBedroom"
                    iconProps={{ size: '1.2em', color: tenantTheme.gray500 }}
                    value={`${item?.beds} ${t('Rooms')}`}
                  />
                )}
                {/* {item?.baths > 0 && (
                  <TextWithIcon
                    icon="IconBathroom"
                    iconProps={{ size: '1.2em', color: tenantTheme.gray500 }}
                    value={`${item?.baths} ${t('Baths')}`}
                  />
                )} */}
                <TextWithIcon
                  icon="IconAreaSize"
                  iconProps={{ size: '1.4em', color: tenantTheme.gray500 }}
                  value={`${item?.area_unit?.value} ${t('Sq. M.')}`}
                />
              </Space>
              <div>
                <TextWithIcon
                  align="center"
                  icon="MdLocationOn"
                  gap="6px"
                  iconProps={{ size: '12px', color: tenantTheme.gray500 }}
                  value={item?.location?.breadcrumb
                    ?.filter((e) => e?.level > 1)
                    ?.map((e) => tenantUtils.getLocalisedString(e, 'title'))
                    ?.join(', ')}
                  textStyle={{ lineHeight: 1 }}
                ></TextWithIcon>
              </div>
            </div>
            <span className="fz-10">
              <Text className="text-muted" style={{ fontSize: 10 }}>
                {t('ID')}
              </Text>{' '}
              <Text className="text-primary" style={{ fontSize: 10 }}>
                {item?.id}
              </Text>
              {isUnifiedListings && (
                <span style={{ display: 'block', marginTop: 4 }}>
                  <Text className="text-muted" style={{ fontSize: 10 }}>
                    {t('Assigned To')}
                  </Text>{' '}
                  <Text className="text-primary" style={{ fontSize: 10 }}>
                    {item?.property?.assignedTo || tenantUtils.getLocalisedString(item?.user, 'name') || '—'}
                  </Text>
                </span>
              )}
            </span>
          </Group>

          {<ListingDrawer ref={listingDrawerRef} />}
        </Group>
        {unifiedMobileStatsSlug && (
          <MobileListingStatsBar platforms={item.platforms} slug={unifiedMobileStatsSlug} />
        )}
        {isMobile && !isUnifiedListings && <Divider style={{ marginBlock: '16px' }} />}

        {isUnifiedListings && item?.platforms?.unified ? (
          <PlatformListing
            slug={'unified'}
            {...item?.platforms}
            inlineActionsSlot={isMobile ? platformActionsInlineSlot : undefined}
          />
        ) : (
          <>
            {item?.platforms?.bayut && (
              <PlatformListing
                slug={'bayut'}
                {...item?.platforms}
                inlineActionsSlot={firstPlatformSlugForInlineActions === 'bayut' ? platformActionsInlineSlot : undefined}
              />
            )}
            {item?.platforms?.dubizzle && (
              <PlatformListing
                slug={'dubizzle'}
                {...item?.platforms}
                inlineActionsSlot={firstPlatformSlugForInlineActions === 'dubizzle' ? platformActionsInlineSlot : undefined}
              />
            )}
          </>
        )}

        {!(isMobile && isUnifiedListings) && (
          <Flex justify="space-between" gap="8px">
            <PlatformActions
              {...item?.platforms}
              isUnifiedListings={isUnifiedListings && isMobile}
              unifiedListingActionLabels={isUnifiedListings}
            />
            {isUnifiedListings ? (
              <UnifiedListingTableActions {...item?.platforms} listingRow={item} />
            ) : (
              <TenantComponents.ListingsRowActions {...item?.platforms} />
            )}
          </Flex>
        )}
      </ListingCardStyled>
    </div>
  );
};

export default ListingCard;
